// PRD 10 §4.2 / §5 — 기본 이미지(placeholder). The spec asks for the same default
// image in two situations, so both share this module: a content row with no
// thumbnail at all (§4.2 "없을 경우 기본 이미지 표시") and a stored URL that fails to
// load (§5 "이미지 로드 실패 → 기본 이미지(placeholder) 표시").
//
// SHCT-716 — the reporter supplied the exact asset to use, so this is a real
// <img>, not an icon glyph. The asset is square (128×128) while the list cell is
// 60×40, hence `object-fit: contain` over a filled background: `cover` would crop
// the photo-frame outline out of the icon, and a bare `contain` would leave the
// cell visually narrower than the rows around it.

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import defaultThumbnail from '@/shared/assets/image/default-image-placeholder-128.png';

const PLACEHOLDER_BACKGROUND = 'var(--muted)';

/**
 * The default image on its own. `style` carries the host cell's box (size,
 * radius) and may override the background — the phone preview, for instance,
 * renders on the patient app's own palette rather than the admin tokens.
 * `object-fit` is applied last because it is the decision, not a caller's choice.
 */
export function ThumbnailPlaceholder({ style }: { style?: CSSProperties }) {
  const { t } = useTranslation('health-encyclopedia');

  return (
    <img
      src={defaultThumbnail}
      alt={t('thumbnail.defaultAlt')}
      style={{ background: PLACEHOLDER_BACKGROUND, ...style, objectFit: 'contain' }}
    />
  );
}

interface ThumbnailWithFallbackProps {
  /** A stored thumbnail URL. Callers decide what an absent URL looks like. */
  url: string;
  /** Non-empty so a failure that outruns `onError` — or an images-off / screen-reader
   *  session — still identifies what the image belongs to. */
  alt: string;
  style?: CSSProperties;
}

export function ThumbnailWithFallback({ url, alt, style }: ThumbnailWithFallbackProps) {
  const [failed, setFailed] = useState(false);

  // A row can be recycled onto a different content item as the list re-sorts or
  // paginates; without this the previous item's failure would suppress a
  // perfectly good image.
  useEffect(() => setFailed(false), [url]);

  if (failed) return <ThumbnailPlaceholder style={style} />;

  return <img src={url} alt={alt} onError={() => setFailed(true)} style={style} />;
}
